import { Vue, Component, Ref, Prop, Emit } from 'vue-property-decorator'
import {
    ConfigurationAutomaticBillableConceptRuleModalState,
    initialConfigurationAutomaticBillableConceptRuleModalState
} from '@/modules/configurationModule/detail/automation/billable-concepts/components/billable-concept-rules-modal/configuration-automatic-billable-concept-rules.model'
import {
    ConfigurationAutomaticBillableConceptRuleCreateRequest,
    ConfigurationAutomaticBillableConceptRuleEntityType,
    ConfigurationAutomaticBillableConceptRuleEntityTypeId,
    ConfigurationAutomaticBillableConceptRulePatchInBatchRequest,
    ConfigurationAutomaticBillableConceptRuleResponse
} from '@/modules/configurationModule/detail/automation/billable-concepts/components/services/configuration-automatic-billable-concept/configuration-automatic-billable-concept-rule.dto'
import {
    configurationBillableConceptService
} from '@/modules/configurationModule/detail/accounting/billable/concepts/services/configuration-billable-concept.service'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { commonCaseTypeService } from '@/api/services/common/case-type/common-case-type.service'
import { CommonCaseTypeResponse } from '@/api/services/common/case-type/common-case-type.dto'
import {
    ConfigurationBillableConceptResponse
} from '@/modules/configurationModule/detail/accounting/billable/concepts/services/configuration-billable-concept.dto'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { commonSampleTypeService } from '@/api/services/common/sample-type/common-sample-type.service'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import { commonTopographicCodeService } from '@/api/services/common/topographic-code/common-topographic-code.service'
import { CommonProcedureResponse } from '@/api/services/common/procedure/common-procedure.dto'
import { commonProcedureService } from '@/api/services/common/procedure/common-procedure.service'
import {
    CommonMostLikelyMorphologicCodeResponse
} from '@/api/services/common/most-likely-morphologic-code/common-most-likely-morphologic-code.dto'
import {
    commonMostLikelyMorphologicCodeService
} from '@/api/services/common/most-likely-morphologic-code/common-most-likely-morphologic-code.service'
import { commonProtocolService } from '@/api/services/common/protocol/common-protocol.service'
import { assaysViewAssayService } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.service'
import { CommonAssayResponse } from '@/api/services/common/assay/common-assay.dto'
import { ProtocolIndexResponse } from '@/api/services/common/protocol/common-protocol.dto'
import { EntityStatusState } from '@/store/root/root-store.model'

@Component({})
export default class ConfigurationAutomaticBillableConceptRulesModal extends Vue {
    @Ref() readonly configurationAutomaticBillableConceptRules!: any

    @Prop(String) readonly actionTitle!: string

    @Emit()
    create(): ConfigurationAutomaticBillableConceptRuleCreateRequest {
        return {
            entityType: this.state.entityType?.code,
            active: this.state.active,
            rulePriority: this.state.rulePriority,
            conceptId: this.state.concept!.id,
            caseTypeId: this.state.caseType?.id ?? null,
            topographicCodeId: this.state.topographicCode?.id,
            mostLikelyMorphologicCodeId: this.state.mostLikelyMorphologicCode?.id,
            sampleTypeId: this.state.sampleType?.id,
            procedureId: this.state.procedure?.id,
            protocolId: this.state.protocol?.prtId,
            assayId: this.state.assay?.id
        }
    }

    @Emit()
    update(): ConfigurationAutomaticBillableConceptRulePatchInBatchRequest {
        return {
            id: this.state.id,
            entityType: this.state.entityType?.code,
            active: this.state.active,
            rulePriority: this.state.rulePriority,
            conceptId: this.state.concept!.id,
            caseTypeId: this.state.caseType?.id ?? null,
            topographicCodeId: this.state.topographicCode?.id ?? null,
            mostLikelyMorphologicCodeId: this.state.mostLikelyMorphologicCode?.id ?? null,
            sampleTypeId: this.state.sampleType?.id ?? null,
            procedureId: this.state.procedure?.id ?? null,
            protocolId: this.state.protocol?.prtId ?? null,
            assayId: this.state.assay?.id ?? null
        }
    }

    state: ConfigurationAutomaticBillableConceptRuleModalState
    constructor() {
        super()
        this.state = initialConfigurationAutomaticBillableConceptRuleModalState()
    }

    async show(configurationAutomaticBillableConceptRule?: ConfigurationAutomaticBillableConceptRuleResponse): Promise<void> {
        await this.$validator.reset()

        this.state = initialConfigurationAutomaticBillableConceptRuleModalState()
        if (configurationAutomaticBillableConceptRule) {
            this.state = {
                ...configurationAutomaticBillableConceptRule,
                entityType: {
                    code: configurationAutomaticBillableConceptRule.entityType.code,
                    name: configurationAutomaticBillableConceptRule.entityType.translatedName
                }
            }
        }
        this.configurationAutomaticBillableConceptRules.show()
    }

    hide() {
        this.configurationAutomaticBillableConceptRules.hide()
    }

    get automationTypes(): EntityStatusState[] {
        return Object.values(ConfigurationAutomaticBillableConceptRuleEntityType)
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.$validator.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get loggedInLaboratoryId(): number {
        return this.$store.getters.labId
    }

    private get _updateMode(): boolean {
        return (this.state.id !== -1)
    }

    get conceptCode(): string {
        return this.state.concept?.code ?? ''
    }

    get requiredCaseTypeRule(): string {
        return this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.CASE ? 'required' : ''
    }

    get requiredProtocolRule(): string {
        return this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.PORTION ? 'required' : ''
    }

    get requiredAssayRule(): string {
        return this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.SUBPORTION ? 'required' : ''
    }

    get requiredOntologyRule(): string {
        const someOntologyIsSelected = this.state.sampleType || this.state.topographicCode || this.state.mostLikelyMorphologicCode || this.state.procedure
        return !someOntologyIsSelected && this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.SAMPLE ? 'required' : ''
    }

    get disabledOntologyCombos(): boolean {
        return this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.CASE
    }

    get disabledAssayCombo(): boolean {
        return this.state.entityType?.code !== ConfigurationAutomaticBillableConceptRuleEntityTypeId.SUBPORTION
    }

    get disabledProtocolCombo(): boolean {
        return this.state.entityType?.code !== ConfigurationAutomaticBillableConceptRuleEntityTypeId.PORTION
    }

    onEntityTypeChange() {
        if (this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.CASE) {
            this.state.sampleType = null
            this.state.topographicCode = null
            this.state.procedure = null
            this.state.mostLikelyMorphologicCode = null
            this.state.protocol = null
            this.state.assay = null
        }
        if (this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.SAMPLE) {
            this.state.protocol = null
            this.state.assay = null
        }
        if (this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.PORTION) {
            this.state.assay = null
        }
        if (this.state.entityType?.code === ConfigurationAutomaticBillableConceptRuleEntityTypeId.SUBPORTION) {
            this.state.protocol = null
        }
    }

    private async _getBillableConcepts(): Promise<ConfigurationBillableConceptResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }
        const response = await this.$_toCallUnique(configurationBillableConceptService.paginator(filterModel, params))
        return response?.content ?? []
    }

    private async _getCaseTypes(): Promise<CommonCaseTypeResponse[]> {
        const response = await this.$_toCallUnique(commonCaseTypeService.getCaseTypes())
        return response ?? []
    }

    private async _getSampleTypes(): Promise<CommonSampleTypeResponse[]> {
        const response = await this.$_toCallUnique(commonSampleTypeService.getSampleTypes())
        return response ?? []
    }

    private async _getTopographicCodes(): Promise<CommonTopographicCodeResponse[]> {
        const response = await this.$_toCallUnique(commonTopographicCodeService.getTopographicCodes())
        return response ?? []
    }

    private async _getProcedures(): Promise<CommonProcedureResponse[]> {
        const response = await this.$_toCallUnique(commonProcedureService.getProcedures())
        return response ?? []
    }

    private async _getMostLikelyMorphologicCodes(): Promise<CommonMostLikelyMorphologicCodeResponse[]> {
        const response = await this.$_toCallUnique(commonMostLikelyMorphologicCodeService.getMostLikelyMorphologicCodes())
        return response ?? []
    }

    private async _getProtocols(): Promise<ProtocolIndexResponse[]> {
        const response = await this.$_toCallUnique(commonProtocolService.getProtocols(this.loggedInLaboratoryId))
        return response ?? []
    }

    private async _getAssays(): Promise<CommonAssayResponse[]> {
        const response = await this.$_toCallUnique(assaysViewAssayService.getAssays(this.loggedInLaboratoryId))
        return response ?? []
    }

    async doAction(): Promise<void> {
        const valid = await this.$validator.validateAll()
        if (valid) {
            if (this._updateMode) {
                this.update() // emits an event
            } else {
                this.create() // emits an event
            }
        }
    }
}
